'use client'

import { Disclosure, Transition } from '@headlessui/react'

import { EXPIRY_BUCKET_DAYS } from 'core/profile'
import type { PointExpirySummaryModel } from 'core/profile/types'
import { useTranslation } from 'app/i18n/client'
import formatToPoint from 'utils/formatToPoint'

import PlusIcon from 'public/icons/plus.svg'
import MinusIcon from 'public/icons/minus.svg'

interface PointExpiryBlockProps {
  expiry: PointExpirySummaryModel
}

const PointExpiryBlock: React.FC<PointExpiryBlockProps> = ({ expiry }) => {
  const { t } = useTranslation()

  return (
    <Disclosure>
      {({ open }: { open: boolean }) => (
        <div className="bg-[#FAFAFA] flex flex-col gap-4 px-4 py-4 lg:gap-6 lg:px-8 lg:py-6">
          <div className="flex justify-between">
            <div className="text-sm font-medium text-gray-dark lg:text-lg lg:leading-[1.4]">
              {t('Expires within {{days}} days', { days: 60 })}
            </div>
            <div className="text-2xl font-lora font-normal leading-none text-error text-right">
              {formatToPoint(expiry.within60Days)}
            </div>
          </div>

          <hr className="w-full border-t border-gray-150" />

          <Transition
            show={open}
            enter="transition duration-100 ease-in-out"
            enterFrom="transform scale-y-0 opacity-0"
            enterTo="transform scale-y-100 opacity-100"
            leave="transition duration-75 ease-in-out"
            leaveFrom="transform scale-y-100 opacity-100"
            leaveTo="transform scale-y-0 opacity-0"
          >
            <Disclosure.Panel static className="flex flex-col gap-4">
              {EXPIRY_BUCKET_DAYS.map((days) => (
                <div key={days} className="flex justify-between">
                  <div className="text-sm font-light text-secondary lg:text-base">
                    {t('Expires within {{days}} days', { days })}
                  </div>
                  <div className="text-lg font-lora font-normal text-gray-dark lg:text-xl">
                    {formatToPoint(expiry.buckets[days])}
                  </div>
                </div>
              ))}
            </Disclosure.Panel>
          </Transition>

          <p className="text-xs leading-[1.7] text-gray-light">
            {t('Points with the earliest expiration date will be used first')}
          </p>

          <Disclosure.Button className="self-center flex items-center justify-center gap-4 h-7 border border-primary px-4 text-xs tracking-[1px] text-primary lg:h-auto lg:py-2.5 lg:text-base lg:leading-[1.6] lg:tracking-normal">
            {open ? (
              <>
                {t('Close section')}
                <MinusIcon className="w-4 h-4 flex-none" />
              </>
            ) : (
              <>
                {t('Show all')}
                <PlusIcon className="w-4 h-4 flex-none" />
              </>
            )}
          </Disclosure.Button>
        </div>
      )}
    </Disclosure>
  )
}

export default PointExpiryBlock
